package at.apa.statistictracker.configuration

import com.squareup.sqldelight.db.SqlDriver
import kotlin.js.ExperimentalJsExport
import kotlin.js.JsExport
import kotlin.js.JsName

@JsExport
class ConfigurationBuilder private constructor(
    override val sqlDriver: SqlDriver,
    override val endpointConfig: EndpointConfig,
    override val initialDispatchDelay: Int,
    override val dispatchInterval: Int,
    override val debuggable: Boolean,
    override val dispatchBundleSize: Int
) : Configuration {

    data class Builder(
        val sqlDriver: SqlDriver,
        val endpointConfig: EndpointConfig,
        var initialDispatchDelay: Int = ConfigurationDefaults.INITIAL_DISPATCH_DELAY,
        var dispatchInterval: Int = ConfigurationDefaults.DISPATCH_INTERVAL,
        var debuggable: Boolean = ConfigurationDefaults.DEBUGGABLE,
        var dispatchBundleSize: Int = ConfigurationDefaults.DISPATCH_BUNDLE_SIZE,
    ) {

        init {
            require(initialDispatchDelay >= 0) { "Configuration parameter initialDispatchDelay must be greater than or equal to 0" }
            require(dispatchInterval >= 10) { "Configuration parameter dispatchInterval must be greater than or equal to 10" }
            require(dispatchBundleSize in 1..100) { "Configuration parameter dispatchBundleSize must be greater than 0 and smaller than or equal to 100" }
        }

        // constructor for ios
        @JsName("jsConstructor")
        constructor(sqlDriver: SqlDriver, endpointConfig: EndpointConfig) : this(
            sqlDriver,
            endpointConfig,
            ConfigurationDefaults.INITIAL_DISPATCH_DELAY,
            ConfigurationDefaults.DISPATCH_INTERVAL,
            ConfigurationDefaults.DEBUGGABLE,
            ConfigurationDefaults.DISPATCH_BUNDLE_SIZE,
        )

        @JsName("setInitialDispatchDelay")
        fun setInitialDispatchDelay(initialDispatchDelay: Int) = apply {
            require(initialDispatchDelay >= 0) { "Configuration parameter initialDispatchDelay must be greater or equal to 0" }
            this.initialDispatchDelay = initialDispatchDelay
        }

        @JsName("setDispatchInterval")
        fun setDispatchInterval(dispatchInterval: Int) = apply {
            require(dispatchInterval >= 10) { "Configuration parameter dispatchInterval must be greater than or equal to 10" }
            this.dispatchInterval = dispatchInterval
        }

        @JsName("setDebuggable")
        fun setDebuggable(debuggable: Boolean) = apply {
            this.debuggable = debuggable
        }

        @JsName("setDispatchBundleSize")
        fun setDispatchBundleSize(dispatchBundleSize: Int) = apply {
            require(dispatchBundleSize in 1..100) { "Configuration parameter dispatchBundleSize must be greater than 0 and smaller than or equal to 100" }
            this.dispatchBundleSize = dispatchBundleSize
        }

        @JsName("build")
        fun build(): Configuration = ConfigurationBuilder(
            sqlDriver,
            endpointConfig,
            initialDispatchDelay,
            dispatchInterval,
            debuggable,
            dispatchBundleSize
        )
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is ConfigurationBuilder) return false

        if (sqlDriver != other.sqlDriver) return false
        if (endpointConfig != other.endpointConfig) return false
        if (initialDispatchDelay != other.initialDispatchDelay) return false
        if (dispatchInterval != other.dispatchInterval) return false
        if (debuggable != other.debuggable) return false
        if (dispatchBundleSize != other.dispatchBundleSize) return false

        return true
    }

    override fun hashCode(): Int {
        var result = sqlDriver.hashCode()
        result = 31 * result + endpointConfig.hashCode()
        result = 31 * result + initialDispatchDelay.hashCode()
        result = 31 * result + dispatchInterval.hashCode()
        result = 31 * result + debuggable.hashCode()
        result = 31 * result + dispatchBundleSize
        return result
    }

    override fun toString(): String {
        return "ConfigurationBuilder(" +
                "sqlDriver=$sqlDriver, " +
                "endpointConfig=$endpointConfig, " +
                "initialDispatchDelay=$initialDispatchDelay, " +
                "dispatchInterval=$dispatchInterval, " +
                "debuggable=$debuggable" +
                "dispatchBundleSize=$dispatchBundleSize" +
                ")"
    }

}